import React, {useRef, useState, useEffect} from "react"
import TmsDashboard from "./components/TmsDashboard"
import CommentSummary from "./components/CommentSummary"
import ActionsSummary from "./components/ActionsSummary"
import ComponentWrapper from "../../common/components/ComponentWrapper"
import {useCustomer} from "../../common/contexts/CustomerContext"
import {useGetCustomerCapacityReport} from "./api/queries"
import html2canvas from "html2canvas"
import jsPDF from "jspdf"
import {useUserPreferences} from "../../common/contexts/UserPreferencesContext"
import Spinner from "@murphy-frontend/web-core/components/Spinner"

function transformActions(capacityReport: any): Record<number, {title: string; description: string}[]> {
	const result: Record<number, {title: string; description: string}[]> = {}
	capacityReport?.actionSummaryResponses?.actionCategories?.forEach((category: any) => {
		const items: {title: string; description: string}[] = []
		category.blockAction?.forEach((block: any) => {
			const description = block.actions.map((act: any) => `${act.status}: ${act.action}`).join("\n")
			items.push({title: block.Title, description})
		})
		result[category.categoryId] = items
	})
	return result
}

function transformComments(capacityReport: any): Record<number, {title: string; description: string}[]> {
	const result: Record<number, {title: string; description: string}[]> = {}
	capacityReport?.commentSummaryResponses?.commentCategories?.forEach((category: any) => {
		const items: {title: string; description: string}[] = []
		category.blockComments?.forEach((block: any) => {
			const description = block.comments.map((c: any) => c.comment).join("\n")
			items.push({title: block.Title, description})
		})
		result[category.categoryId] = items
	})
	return result
}

function tmsAssessmentResult() {
	const pdfRef = useRef<HTMLDivElement>(null)
	const pdfDashboardRef = useRef<HTMLDivElement>(null)
	const pdfCommentsRef = useRef<HTMLDivElement>(null)
	const pdfActionsRef = useRef<HTMLDivElement>(null)
	const {translations} = useUserPreferences()
	const [commentsExpanded, setCommentsExpanded] = useState<boolean>(true)
	const [actionsExpanded, setActionsExpanded] = useState<boolean>(false)
	const [showPdfContent, setShowPdfContent] = useState<boolean>(false)

	const originalStatesRef = useRef({commentsExpanded, actionsExpanded})

	const {customer} = useCustomer()
	const customerId = customer?.Id || ""
	const {data: capacityReport, isLoading, isError} = useGetCustomerCapacityReport(customerId)

	const actionsData = capacityReport ? transformActions(capacityReport) : {}
	const commentsData = capacityReport ? transformComments(capacityReport) : {}

	const capturePDF = async () => {
		try {
			if (!pdfRef.current) return

			const pdf = new jsPDF({
				orientation: "portrait",
				unit: "mm",
				format: "a4"
			})

			const pageWidth = 210
			const pageHeight = 297
			let position = 0

			const addSectionToPDF = async (element: HTMLElement | null, title: string) => {
				if (!element) return

				const originalWidth = element.style.width
				const originalMaxWidth = element.style.maxWidth
				element.style.width = "100%"
				element.style.maxWidth = "none"

				const canvas = await html2canvas(element, {
					scale: 2,
					useCORS: true,
					allowTaint: true,
					backgroundColor: "#ffffff",
					logging: true,
					width: element.scrollWidth
				})

				element.style.width = originalWidth
				element.style.maxWidth = originalMaxWidth

				const imgData = canvas.toDataURL("image/png")
				const imgWidth = pageWidth - 10 // 5mm margin on each side
				const imgHeight = (canvas.height * imgWidth) / canvas.width

				console.log(
					`Adding ${title} - imgHeight: ${imgHeight}mm, current position: ${position}mm, remaining space: ${pageHeight - position}mm`
				)

				if (position + imgHeight > pageHeight && position > 0) {
					pdf.addPage()
					position = 0
					console.log(`New page added for ${title}`)
				}

				pdf.addImage(imgData, "PNG", 5, position, imgWidth, imgHeight)
				position += imgHeight
				position += 5 // Margin between sections
				console.log(`After adding ${title}, new position: ${position}mm`)
			}

			// Add the header first
			const headerElement = pdfRef.current.querySelector(".dashboard__header") as HTMLElement
			if (headerElement) await addSectionToPDF(headerElement, "Header")

			// Add TmsDashboard
			if (pdfDashboardRef.current) await addSectionToPDF(pdfDashboardRef.current, "Dashboard")

			// Add CommentSummary: Header first, then categories
			if (pdfCommentsRef.current) {
				// Capture the CommentSummary header
				const commentsHeader = pdfCommentsRef.current.querySelector(".comment-summary__header") as HTMLElement
				if (commentsHeader) await addSectionToPDF(commentsHeader, "Comments Header")

				// Capture the categories
				const categories = pdfCommentsRef.current.querySelectorAll(".category-section")
				for (const [index, categoryElement] of Array.from(categories).entries()) {
					const categoryTitle = `Comment Category ${index + 1}`
					await addSectionToPDF(categoryElement as HTMLElement, categoryTitle)
				}
			}

			// Add ActionsSummary: Header first, then categories
			if (pdfActionsRef.current) {
				// Capture the ActionsSummary header
				const actionsHeader = pdfActionsRef.current.querySelector(".actions-summary__header") as HTMLElement
				if (actionsHeader) await addSectionToPDF(actionsHeader, "Actions Header")

				// Capture the categories
				const categories = pdfActionsRef.current.querySelectorAll(".category-section")
				for (const [index, categoryElement] of Array.from(categories).entries()) {
					const categoryTitle = `Action Category ${index + 1}`
					await addSectionToPDF(categoryElement as HTMLElement, categoryTitle)
				}
			}

			pdf.save("assessment-result.pdf")
		} catch (error: any) {
			console.error("Error generating PDF:", error)
			alert(`Failed to generate PDF: ${error.message}`)
		} finally {
			setShowPdfContent(false)
			setCommentsExpanded(originalStatesRef.current.commentsExpanded)
			setActionsExpanded(originalStatesRef.current.actionsExpanded)
		}
	}

	useEffect(() => {
		if (showPdfContent && !isLoading && capacityReport && pdfRef.current) {
			const timer = setTimeout(() => {
				capturePDF()
			}, 500)
			return () => clearTimeout(timer)
		}
	}, [showPdfContent, isLoading, capacityReport, pdfRef])

	const handlePrintToPDF = async () => {
		if (isLoading) {
			alert("Please wait until the data is loaded.")
			return
		}
		if (isError) {
			alert("Cannot generate PDF due to an error loading data.")
			return
		}
		if (!pdfDashboardRef.current || !pdfCommentsRef.current || !pdfActionsRef.current || !pdfRef.current) {
			console.log(pdfDashboardRef, pdfCommentsRef, pdfActionsRef, pdfRef)
			console.error("One or more refs are missing.")
			alert("Failed to find required content elements.")
			return
		}

		originalStatesRef.current = {commentsExpanded, actionsExpanded}
		setCommentsExpanded(true)
		setActionsExpanded(true)
		setShowPdfContent(true)
	}

	if (isLoading)
		return (
			<ComponentWrapper active={3}>
				<div className='spinner-container-global-center'>
					<Spinner />
				</div>
			</ComponentWrapper>
		)
	if (isError) return <div>Error loading data</div>

	return (
		<ComponentWrapper active={3}>
			<div ref={pdfRef}>
				<h1 className='dashboard__header'>
					{translations.tmsAssessmentHeadline || "Your organization's assessment result:"}
				</h1>
				<div ref={pdfDashboardRef}>
					<TmsDashboard customerId={customer?.Id} showExploreButton={false} />
				</div>
				<section className='margin-top-20'>
					<CommentSummary
						ref={pdfCommentsRef}
						isExpanded={commentsExpanded}
						setIsExpanded={setCommentsExpanded}
						showPdfContent={showPdfContent}
						comments={commentsData}
					/>
				</section>
				<section className='margin-top-20'>
					<ActionsSummary
						ref={pdfActionsRef}
						isExpanded={actionsExpanded}
						setIsExpanded={setActionsExpanded}
						showPdfContent={showPdfContent}
						actions={actionsData}
					/>
				</section>

				<div style={{marginTop: "20px", textAlign: "center"}}>
					<button
						onClick={handlePrintToPDF}
						style={{
							padding: "10px 20px",
							backgroundColor: "#4a90e2",
							color: "#fff",
							border: "none",
							borderRadius: "5px",
							cursor: "pointer",
							fontSize: "16px"
						}}
					>
						{translations["lang-filter-dropdown-print-timeline"] || "Print to PDF"}
					</button>
				</div>
			</div>
		</ComponentWrapper>
	)
}

export default tmsAssessmentResult
